// ---------------------------------------------------------------------------
// CreateVariableForm — inline form for creating a new variable.
// ---------------------------------------------------------------------------

import { useState } from 'react'
import { useStudioStore } from '@/store/studio-store'
import { toast } from '@/store/toast-store'
import { cn } from '@/lib/utils'

const VARIABLE_TYPES = ['string', 'number', 'boolean', 'array', 'object'] as const
const VARIABLE_SCOPES = ['state', 'global', 'page', 'local'] as const

interface Props {
  onClose: () => void
  defaultBlockName?: string
}

export function CreateVariableForm({ onClose, defaultBlockName }: Props) {
  const project = useStudioStore((s) => s.currentProject)
  const addVariable = useStudioStore((s) => s.addVariable)
  const blocks = project ? Object.keys(project.blocks) : []

  const [name, setName] = useState('')
  const [type, setType] = useState<string>('string')
  const [scope, setScope] = useState<string>('state')
  const [defaultValue, setDefaultValue] = useState('')
  const [blockName, setBlockName] = useState(defaultBlockName ?? blocks[0] ?? '')

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Variable name is required')
      return
    }
    if (!blockName) {
      toast.error('Select a block')
      return
    }

    const def: { type: string; default?: unknown } = { type }
    if (defaultValue.trim()) {
      if (type === 'number') def.default = Number(defaultValue) || 0
      else if (type === 'boolean') def.default = defaultValue === 'true'
      else if (type === 'array' || type === 'object') {
        try { def.default = JSON.parse(defaultValue) } catch { def.default = defaultValue }
      } else {
        def.default = defaultValue
      }
    }

    addVariable(blockName, scope, name.trim(), def)
    toast.success(`Variable "${name}" created`)
    onClose()
  }

  return (
    <form onSubmit={handleSubmit} className="px-3 py-2 space-y-2 border-b border-border bg-accent/30">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">New Variable</span>
        <button type="button" onClick={onClose} className="text-[10px] text-muted-foreground hover:text-foreground">Cancel</button>
      </div>

      {blocks.length > 1 && (
        <select
          value={blockName}
          onChange={(e) => setBlockName(e.target.value)}
          className="w-full h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {blocks.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
      )}

      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Variable name"
        className="w-full h-7 rounded border border-border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
      />

      <div className="flex gap-2">
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          className="flex-1 h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {VARIABLE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="flex-1 h-7 rounded border border-border bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        >
          {VARIABLE_SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <input
        value={defaultValue}
        onChange={(e) => setDefaultValue(e.target.value)}
        placeholder="Default value (optional)"
        className="w-full h-7 rounded border border-border bg-background px-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring"
      />

      <button
        type="submit"
        className={cn(
          'w-full h-7 rounded text-xs font-medium transition-colors',
          name.trim()
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground cursor-not-allowed',
        )}
        disabled={!name.trim()}
      >
        Create Variable
      </button>
    </form>
  )
}
